import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Checkbox_, Grey_, Main_Loader, PageFull } from 'monica-alexandria';
import { get_public_offer, start_payment, track_public_visit } from '../../../services/api/OfferV3';
import { format_amount } from '../offerEditor/utils/offer_payload';
import Offer_Bank_Transfer from '../../../components/offerPayment/Offer_Bank_Transfer';
import { can_view_public_offer } from '../../../helpers/offerAccess';
import { checkPermissions } from '../../../helpers/checkPermissions';
import { useHrAccess } from '../../../services/authentication/HrAccess';

const OfferPublic = styled(PageFull)`display: grid; justify-items: center; color: ${p => p.theme.color}; padding: var(--normalPads);`;
const Content = styled.article`width: min(100%, 90rem); display: grid; gap: 2rem; section { border-top: 0.1rem solid ${p => p.theme.mid}; padding-top: 1.6rem; } p { white-space: pre-wrap; } @media print { button { display: none; } }`;
const Error = styled.p`color: var(--error);`;
const Acceptance = styled.section`display: grid; gap: 1.2rem; @media print { display: none; }`;

export default function OFFER_PUBLIC() {
    const { t } = useTranslation(); const { id, lang } = useParams(); const navigate = useNavigate(); const [params] = useSearchParams();
    const user = useSelector(state => state.user.user || {});
    const { isReady: isHrReady, isLoading: isHrLoading, permissions } = useHrAccess();
    const hasAcceptedTeamMembership = permissions.hasAcceptedTeamMembership === true;
    const rolesResolved = user.rolesResolved !== false;
    const hasScoutAccess = checkPermissions('Scout', user.roles || []);
    const revisionId = params.get('revisionId'); const [offer, setOffer] = useState(null); const [error, setError] = useState(''); const [loading, setLoading] = useState(true);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    const [showBankTransfer, setShowBankTransfer] = useState(false);
    const activeProposal = useRef(null);
    const canAcceptTerms = !offer?.fakeOffer && !['replaced', 'stopped'].includes(offer?.serviceActivation?.state) && offer?.availability === 'available'
        && ['sent', 'payment_pending'].includes(offer?.salesStatus)
        && (!offer.commercial.expiresAt || new Date(offer.commercial.expiresAt) >= new Date());
    const canPay = canAcceptTerms && (offer.payment.linkUrl || ['manual', 'bank_transfer'].includes(offer.payment.method));
    const bankTransferCopy = {
        bankTransferDetails: t('Bank transfer details'),
        bankTransferText: t('Use the following details to complete the payment.'),
        beneficiary: t('Beneficiary'),
        reference: t('Payment reference'),
        amount: t('Amount'),
        close: t('Close')
    };
    useEffect(() => {
        let mounted = true;
        const proposalRequest = { id, revisionId };
        activeProposal.current = proposalRequest;
        setLoading(true); setOffer(null); setError(''); setTermsAccepted(false); setShowBankTransfer(false); setIsPaying(false);
        get_public_offer(id, revisionId).then(response => {
            if (!mounted) return;
            const data = response.data.data;
            if (!can_view_public_offer(data, user.roles || [])) { navigate(`/${lang}/access-denied`, { replace: true }); return; }
            if (data.preservedProposal) { navigate(`/${lang}/offer/${encodeURIComponent(data.preservedProposal.offerId)}?api=${data.preservedProposal.apiMode === 'agency' ? 'agency' : 'legacy'}`, { replace: true }); return; }
            setOffer(data);
        })
            .catch(failure => {
                if (!mounted) return;
                if ([401, 403].includes(failure.response?.status)) { navigate(`/${lang}/access-denied`, { replace: true }); return; }
                setError(failure.response?.data?.message || t('Proposal unavailable.'));
            })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; if (activeProposal.current === proposalRequest) activeProposal.current = null; };
    }, [id, revisionId, t, lang, navigate, hasScoutAccess]);
    useEffect(() => {
        if (offer?.id !== id || loading || !rolesResolved || !isHrReady || isHrLoading || hasAcceptedTeamMembership || offer.fakeOffer) return;
        const visitKey = `offer-v3-visit:${id}`;
        if (sessionStorage.getItem(visitKey)) return;
        const key = window.crypto.randomUUID();
        track_public_visit(id, key).then(() => sessionStorage.setItem(visitKey, key)).catch(() => {});
    }, [id, offer, loading, rolesResolved, isHrReady, isHrLoading, hasAcceptedTeamMembership]);
    const pay = async() => {
        if (!canPay || !termsAccepted || isPaying) return;
        const proposalRequest = activeProposal.current;
        setIsPaying(true); setError('');
        try {
            const response = await start_payment(id, offer.proposal.revisionId);
            if (activeProposal.current !== proposalRequest) return;
            setOffer(current => ({ ...current, salesStatus: 'payment_pending' }));
            if (response.data.data.linkUrl) window.location.assign(response.data.data.linkUrl);
            else setShowBankTransfer(true);
        }
        catch (failure) { if (activeProposal.current === proposalRequest) setError(failure.response?.data?.message || t('Payment is unavailable.')); }
        finally { if (activeProposal.current === proposalRequest) setIsPaying(false); }
    };
    let paymentText = t('Pay');
    if (isPaying) paymentText = t('Processing...');
    else if (offer && !offer.payment.linkUrl) paymentText = t('Proceed with bank transfer');
    return <OfferPublic>{loading ? <Main_Loader></Main_Loader> : null}{error ? <Error role="alert">{error}</Error> : null}
        {offer && showBankTransfer ? <Offer_Bank_Transfer
            t={t}
            copy={bankTransferCopy}
            offerCode={offer.code}
            amount={offer.totals.grossMinor / 100}
            onClose={() => setShowBankTransfer(false)}
        ></Offer_Bank_Transfer> : null}
        {offer ? <Content>
            <header><h1>{t('Proposal')} {offer.code}</h1><h2>{offer.brand.internalName}</h2><p>{offer.brand.address || offer.brand.location || ''}</p></header>
            <p>{offer.proposal.overview}</p>
            {offer.services.map(line => <section key={line.lineId}><h3>{line.name}</h3><p>{line.scope.description}</p>
                {line.scope.answers.map(answer => <p key={answer.key}>{t(answer.key)}: {answer.value}</p>)}<p>{line.customerComment}</p><p>{t('Quantity')}: {line.quantity}</p>
            </section>)}
            <section><p>{t('Before VAT')}: {format_amount(offer.totals.netMinor, offer.commercial.currency)}</p><p>{t('VAT')}: {format_amount(offer.totals.vatMinor, offer.commercial.currency)}</p><h2>{t('Total')}: {format_amount(offer.totals.grossMinor, offer.commercial.currency)}</h2><p>{t('Billing interval')}: {t(offer.commercial.billingInterval)}</p><p>{t('Valid until')}: {offer.commercial.expiresAt ? new Date(offer.commercial.expiresAt).toLocaleDateString() : ''}</p><p>{t('Sale')}: {t(offer.salesStatus)}</p></section>
            {offer.availability !== 'available' ? <p>{t('Proposal unavailable for payment')}: {t(offer.availability)}</p> : null}
            {canAcceptTerms ? <Acceptance>
                <h3>{t('Offer terms acceptance')}</h3>
                <Checkbox_
                    id="offer-public-terms"
                    text={t('I formally confirm that I have fully reviewed the content, terms and financial provisions of this offer and I accept them in full without reservation.')}
                    checked={termsAccepted}
                    onChange={event => { if (!isPaying) setTermsAccepted(event.target.checked); }}
                ></Checkbox_>
                {canPay ? <Grey_ text={paymentText} disabled={!termsAccepted || isPaying} onClick={pay}></Grey_> : <p>{t('Payment is unavailable.')}</p>}
            </Acceptance> : null}
            <Grey_ text={t('Print proposal')} onClick={() => window.print()}></Grey_>
        </Content> : null}
    </OfferPublic>;
}
